import * as React from 'react';
import gql from 'graphql-tag';
import { graphql, DataProps } from 'react-apollo';
import invariant from 'invariant';
import CurrentUser from 'auth/CurrentUser';

import FEATURE_QUERY from 'components/queries/FetchFeaturesQuery.gql';
import { FetchFeature, FetchFeatureVariables } from 'components/queries/__generated__/FetchFeature';

type WrapperProps = {
  name: string;
  on?: React.ReactElement<any>;
  off?: React.ReactElement<any>;
  children?: React.ReactElement<any>;
  injector?: (feature: string) => any;
};

type Props = {
  loading: boolean;
  hasFeature: boolean;
};

export const withFeature = function<P>(feature: string, propName?: string) {
  return graphql<P, FetchFeature, FetchFeatureVariables, any>(FEATURE_QUERY, {
    options: () => ({ variables: { feature } }),
    props: ({ data }) => ({
      loading: data && data.loading,
      [propName || 'hasFeature']: data && data.user ? data && !!data.user.feature : undefined,
    }),
  });
};

/* Render component based on the current user feature flag
 * Only a success handler is required: either the "on" prop or passing children.
 * null is rendered when the feature flag in turned off and "off" props is undefined.
 *
 * <Feature name="some_cool_feature" on={<SomeCoolComponent />} off={<SomeOldComponent />} />
 *
 * <Feature name="some_cool_feature">
 *   <SomeCoolComponent />
 * </Feature>
 */
function Feature(wrapperProps: WrapperProps): React.ReactElement<any> | null {
  const { name, on, off, injector, children } = wrapperProps;

  invariant(name, 'Feature - cannot render without a feature name');
  invariant(
    !on || !children,
    'Feature - what did you do? No both! I said "or" as in xor, you robot.'
  );

  const onCase = on || children || null;
  const offCase = off || null;

  if (!CurrentUser.isSignedIn() || CurrentUser.isLimitedUserAuth()) {
    // anon user can't have features
    return offCase;
  }

  const FeatureComponent = (injector || withFeature)(name)(function(props: Props) {
    const { hasFeature, loading } = props;

    if (loading || hasFeature === undefined) {
      return null;
    }

    return hasFeature ? onCase : offCase;
  });

  return <FeatureComponent />;
}

export { Feature };
export default Feature;
